import React, { useState, useEffect, useRef } from 'react';
import SysOwnerSideBar from './SysOwnerSidebar';
import { useParams, useNavigate } from 'react-router-dom';
import * as mui from '@mui/material';
import ResponsiveContainer from '../../layout/Container';
import SearchAppBar from '../../common/Appbar';
import Separator from '../../layout/Separator';
import GridViewIcon from '@mui/icons-material/GridView';
import Paper from '@mui/material/Paper';
import { styled, alpha } from '@mui/material/styles';
import appService from '../../../services/ApplicationService';
import userService from '../../../services/UserService';
import companyService from '../../../services/CompanyService';
import DynamicTabs from '../../common/DynamicTabs';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import NormalTextField from '../../common/NormalTextField';
import MultipleSelect from '../../common/MultipleSelect';
import SwitchesGroup from '../../common/SwitchesGroup';
import appPasswordService from '../../../services/PasswordService';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SaveIcon from '@mui/icons-material/Save';
import DoneIcon from '@mui/icons-material/Done';
// Add questionnaire UI imports
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import QuizIcon from '@mui/icons-material/Quiz';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

// Add questionnaire UI components
const QuestionnaireCard = styled(mui.Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  border: '1px solid',
  borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.1) : alpha(theme.palette.divider, 0.08),
  background: theme.palette.background.paper,
}));

const QuestionTitle = styled(mui.Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  fontSize: '1.25rem',
  color: theme.palette.text.primary,
  '& .MuiSvgIcon-root': {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
}));

const QuestionDescription = styled(mui.Typography)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  color: theme.palette.text.secondary,
}));

const NavigationButtons = styled(mui.Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: theme.spacing(4),
  paddingTop: theme.spacing(3),
  borderTop: '1px solid',
  borderColor: alpha(theme.palette.divider, 0.08),
}));

// Modern minimalist styled components
const StyledCard = styled(mui.Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(1),
  boxShadow: 'none',
  border: '1px solid',
  borderColor: alpha(theme.palette.divider, 0.5),
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.3),
  },
}));

const StyledSection = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(6),
  marginTop: theme.spacing(4),
}));

const SectionTitle = styled(mui.Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(3),
  fontWeight: 500,
  color: theme.palette.text.primary,
  '& .MuiSvgIcon-root': {
    fontSize: '1.2rem',
  },
}));

const FieldGrid = styled(mui.Grid)(({ theme }) => ({
  '& .MuiGrid-item': {
    paddingBottom: theme.spacing(2.5),
  },
}));

const StyledSectionHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
}));

const FloatingAction = styled(mui.Zoom)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(4),
  right: theme.spacing(4),
  zIndex: 1000,
}));

// Saving indicator
const SavingIndicator = styled(mui.Fade)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  zIndex: 9999,
}));

const PillBadge = styled('span')(({ theme, status }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '0.25rem 0.75rem',
  borderRadius: '1rem',
  fontSize: '0.75rem',
  fontWeight: 500,
  marginLeft: theme.spacing(1),
  backgroundColor: status === 'success' 
    ? alpha(theme.palette.success.main, 0.1) 
    : alpha(theme.palette.warning.main, 0.1),
  color: status === 'success' 
    ? theme.palette.success.main 
    : theme.palette.warning.main,
}));

// Custom styled input with lighter borders
const StyledInput = styled(mui.TextField)(({ theme }) => ({
  width: '100%',
  '& .MuiInputLabel-root': {
    fontSize: '0.85rem',
    transform: 'translate(14px, -9px) scale(0.75)',
    backgroundColor: 'white',
    padding: '0 5px',
    color: theme.palette.text.secondary,
  },
  '& .MuiOutlinedInput-input': {
    padding: '10px 14px',
    fontSize: '0.9rem',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: alpha(theme.palette.divider, 0.6),
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: alpha(theme.palette.primary.main, 0.5),
    },
    '&.Mui-focused fieldset': {
      borderColor: alpha(theme.palette.primary.main, 0.7),
      borderWidth: '1px',
    },
  },
}));

// Enhanced version of MultipleSelect with lighter borders
const StyledFormControl = styled(mui.FormControl)(({ theme }) => ({
  width: '100%',
  '& .MuiInputLabel-root': {
    fontSize: '0.85rem',
    transform: 'translate(14px, -9px) scale(0.75)',
    backgroundColor: 'white',
    padding: '0 5px',
    color: theme.palette.text.secondary,
  },
  '& .MuiOutlinedInput-root': {
    '& .MuiSelect-select': {
      padding: '10px 14px',
      fontSize: '0.9rem',
    },
    '& fieldset': {
      borderColor: alpha(theme.palette.divider, 0.6),
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: alpha(theme.palette.primary.main, 0.5),
    },
    '&.Mui-focused fieldset': {
      borderColor: alpha(theme.palette.primary.main, 0.7),
      borderWidth: '1px',
    },
  },
}));

// Enhanced version of NormalTextField that uses our custom styling
const MinimalistTextField = ({
  label, 
  name, 
  value, 
  onChange, 
  onBlur, 
  isMultiLine, 
  rows, 
  readOnly, 
  required, 
  type, 
  min, 
  max,
  pattern,
  helperText,
  error
}) => {
  return (
    <StyledInput
      label={label}
      name={name}
      value={value || ''}
      onChange={onChange}
      onBlur={onBlur}
      multiline={isMultiLine}
      rows={rows}
      disabled={readOnly}
      required={required}
      type={type || 'text'}
      variant="outlined"
      InputProps={{
        readOnly: readOnly,
        inputProps: { min, max, pattern }
      }}
      error={error}
      helperText={error ? helperText : ''}
    />
  );
};

// Modern minimal switch group
const MinimalistSwitchesGroup = ({title, switches, onChange}) => {
  return (
    <mui.Box sx={{ border: '1px solid', borderColor: alpha('#000', 0.08), borderRadius: 1, p: 2 }}>
      <mui.Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
        {title}
      </mui.Typography>
      <mui.Grid container spacing={2}>
        {switches.map((sw) => (
          <mui.Grid item xs={12} md={6} key={sw.name}>
            <mui.FormControlLabel
              control={
                <mui.Switch
                  checked={sw.checked || false}
                  onChange={(e) => onChange(sw.name, e.target.checked)}
                  name={sw.name}
                  size="small"
                />
              }
              label={<mui.Typography variant="body2">{sw.label}</mui.Typography>}
              sx={{ ml: 0 }}
            />
          </mui.Grid>
        ))}
      </mui.Grid>
    </mui.Box>
  );
};

// Enhanced version of MultipleSelect that uses our custom styling
const MinimalistSelect = ({label, placeholderText, selectOptions, selectedOptions, handleChange, isMultiSelect}) => {
  return (
    <StyledFormControl variant="outlined">
      <mui.InputLabel id={`select-${label.replace(/\s+/g, '-').toLowerCase()}`}>{label}</mui.InputLabel>
      <mui.Select
        labelId={`select-${label.replace(/\s+/g, '-').toLowerCase()}`}
        id={`${label.replace(/\s+/g, '-').toLowerCase()}`}
        value={selectedOptions?.value || ''}
        onChange={(e) => {
          const selectedOption = selectOptions.find(option => option.value === e.target.value);
          handleChange(selectedOption);
        }}
        label={label}
        displayEmpty
      >
        <mui.MenuItem value="" disabled>
          <em>{placeholderText}</em>
        </mui.MenuItem>
        {selectOptions.map((option) => (
          <mui.MenuItem key={option.value} value={option.value}>
            {option.label}
          </mui.MenuItem>
        ))}
      </mui.Select>
    </StyledFormControl>
  );
};

// Add styled step button
const CustomStepButton = styled(mui.StepButton)(({ theme, active, completed }) => ({
  '& .MuiStepLabel-root': {
    transition: 'all 0.2s ease',
  },
  '& .MuiStepLabel-label': {
    color: active ? theme.palette.primary.main : (completed ? theme.palette.success.main : theme.palette.text.secondary),
    fontWeight: active ? 600 : (completed ? 500 : 400),
    transition: 'all 0.2s ease',
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    borderRadius: '8px',
    '& .MuiStepLabel-label': {
      color: theme.palette.primary.main,
    },
  },
  '& .MuiStepIcon-root': {
    transition: 'transform 0.2s ease, color 0.2s ease',
    color: active ? theme.palette.primary.main : (completed ? theme.palette.success.main : theme.palette.text.secondary),
  },
  '&:hover .MuiStepIcon-root': {
    transform: 'scale(1.1)',
    color: theme.palette.primary.main,
  },
}));

const MyApplicationGeneralInfo = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [selectedApps, setSelectedApps] = useState({});
    const [selectedType, setformattedType] = useState([]);
    const [selectedHosting, setSelectedHosting] = useState([]);
    const [selectedDev, setSelectedDev] = useState([]);
    const [osList, setOSList] = useState([]);
    const [selectedOS, setSelectedOS] = useState([]);
    const [selectedDB, setSelectedDB] = useState([]);
    const [selectedNetwork, setSelectedNetwork] = useState([]);
    const [selectedAuth, setSelectedAuth] = useState([]);
    const [selectedConfig, setSelectedConfig] = useState([]);
    const [dbList, setDBList] = useState([]);
    const [toolList, setToolList] = useState([]);
    const [networkList, setNetworkList] = useState([]);
    const saveTimeout = useRef(null);
    const [pwExist, setPWExist] = useState(false);

    const [geninfo, setGenInfo] = useState(false);
    const [userdata, setUserData] = useState(false);
    const [process, setProcess] = useState(false);

    const [overview, setOverview] = useState(false);
    const [infrastructure, setInfrastructure] = useState(false);
    const [authentication, setAuthentication] = useState(false);
    const [hostingTypeVisibility, setHostingTypeVisibility] = useState('none');

    const [passwordData, setPasswordData] = useState({});

    // Add new state for questionnaire
    const [activeStep, setActiveStep] = useState(0);
    const [skipped, setSkipped] = useState(new Set());
    
    // Add new state for saving indicator
    const [isSaving, setIsSaving] = useState(false);
    const [saveComplete, setSaveComplete] = useState(false);
    const [saveInProgress, setSaveInProgress] = useState(false);

    // Add validation state
    const [validationErrors, setValidationErrors] = useState({});
    
    // Add menu state
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const isMenuOpen = Boolean(menuAnchorEl);
    
    // Handle menu open
    const handleMenuOpen = (event) => {
        setMenuAnchorEl(event.currentTarget);
    };
    
    // Handle menu close
    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    // Define questionnaire steps
    const steps = [
      { 
        label: 'Application Type', 
        description: 'Let\'s start with some basic information about your application.',
        isRequired: true 
      },
      { 
        label: 'Application Description', 
        description: 'Tell us more about what your application does.',
        isRequired: true 
      },
      { 
        label: 'Development & Hosting', 
        description: 'How was the application developed and where is it hosted?',
        isRequired: true 
      },
      { 
        label: 'Infrastructure Details', 
        description: 'Tell us about the technical infrastructure of your application.',
        isRequired: selectedApps.HOSTED === 'On-premise' 
      },
      { 
        label: 'Authentication Method', 
        description: 'How do users authenticate with your application?',
        isRequired: true 
      },
      { 
        label: 'Password Configuration', 
        description: 'What password policies are configured for your application?',
        isRequired: (selectedApps.AUTHENTICATION_TYPE === 'Native/Direct' || 
                     selectedApps.AUTHENTICATION_TYPE === 'Both Native and SSO') && 
                    selectedApps.PW_CONFIGURABLE === 'Yes'
      }
    ];
    
    // Add step is optional function
    const isStepOptional = (step) => {
      return step === 3 && selectedApps.HOSTED !== 'On-premise';
    };
    
    // Check if a step should be skipped
    const isStepSkipped = (step) => {
      return skipped.has(step);
    };
    
    // Helper validation functions - moved before useEffect that depends on them
    const isNonEmptyString = (value) => typeof value === 'string' && value.trim() !== '';
    const isValidInteger = (value) => {
      // Handle null, undefined, or empty string
      if (value === null || value === undefined || value === '') return false;
      
      // Convert string to number if needed
      const numValue = typeof value === 'string' ? Number(value) : value;
      
      // Check if it's an integer and greater than 0
      return Number.isInteger(numValue) && numValue > 0;
    };
    
    // Handle next step
    const handleNext = () => {
      let newSkipped = skipped;
      if (isStepSkipped(activeStep)) {
        newSkipped = new Set(newSkipped.values());
        newSkipped.delete(activeStep);
      }
      
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      setSkipped(newSkipped);
    };
    
    // Handle back step
    const handleBack = () => {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };
    
    // Handle skip step
    const handleSkip = () => {
      if (!isStepOptional(activeStep)) {
        throw new Error("You can't skip a step that isn't optional.");
      }
      
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      setSkipped((prevSkipped) => {
        const newSkipped = new Set(prevSkipped.values());
        newSkipped.add(activeStep);
        return newSkipped;
      });
    };
    
    // Handle reset
    const handleReset = () => {
      setActiveStep(0);
    };
    
    // Check if current step is complete
    const isStepComplete = (step) => {
      switch(step) {
        case 0: // App Type
          return isNonEmptyString(selectedApps.APP_TYPE);
        case 1: // App Description
          return isNonEmptyString(selectedApps.APP_DESCRIPTION);
        case 2: // Development & Hosting
          return isNonEmptyString(selectedApps.DEVTYPE) && isNonEmptyString(selectedApps.HOSTED);
        case 3: // Infrastructure Details
          if (selectedApps.HOSTED === 'On-premise') {
            return isNonEmptyString(selectedApps.OS) && 
                   isNonEmptyString(selectedApps.OS_VERSION) &&
                   isNonEmptyString(selectedApps.DATABASE) &&
                   isNonEmptyString(selectedApps.DB_VERSION) && 
                   isNonEmptyString(selectedApps.NETWORK) && 
                   isNonEmptyString(selectedApps.NETWORK_VERSION);
          }
          return true;
        case 4: // Authentication Method
          return isNonEmptyString(selectedApps.AUTHENTICATION_TYPE) && 
                 isNonEmptyString(selectedApps.PW_CONFIGURABLE);
        case 5: // Password Configuration
          if ((selectedApps.AUTHENTICATION_TYPE === 'Native/Direct' || 
               selectedApps.AUTHENTICATION_TYPE === 'Both Native and SSO') && 
              selectedApps.PW_CONFIGURABLE === 'Yes') {
            return isValidInteger(passwordData.LENGTH) &&
                   isValidInteger(passwordData.AGE) &&
                   isValidInteger(passwordData.HISTORY) &&
                   isValidInteger(passwordData.LOCKOUT_ATTEMPT);
          }
          return true;
        default:
          return false;
      }
    };
    
    // Add effect for keyboard listeners without dependencies on functions
    useEffect(() => {
      function handleKeyDown(e) {
        // Next step with right/down arrow keys
        if ((e.key === 'ArrowRight' || e.key === 'ArrowDown') && 
            activeStep < steps.length - 1) {
          
          // Check if current step is complete based on activeStep
          let currentStepComplete = false;
          
          switch(activeStep) {
            case 0: // App Type
              currentStepComplete = isNonEmptyString(selectedApps.APP_TYPE);
              break;
            case 1: // App Description
              currentStepComplete = isNonEmptyString(selectedApps.APP_DESCRIPTION);
              break;
            case 2: // Development & Hosting
              currentStepComplete = isNonEmptyString(selectedApps.DEVTYPE) && 
                isNonEmptyString(selectedApps.HOSTED);
              break;
            case 3: // Infrastructure Details
              if (selectedApps.HOSTED === 'On-premise') {
                currentStepComplete = isNonEmptyString(selectedApps.OS) && 
                  isNonEmptyString(selectedApps.OS_VERSION) &&
                  isNonEmptyString(selectedApps.DATABASE) &&
                  isNonEmptyString(selectedApps.DB_VERSION) && 
                  isNonEmptyString(selectedApps.NETWORK) && 
                  isNonEmptyString(selectedApps.NETWORK_VERSION);
              } else {
                currentStepComplete = true;
              }
              break;
            case 4: // Authentication Method
              currentStepComplete = isNonEmptyString(selectedApps.AUTHENTICATION_TYPE) && 
                isNonEmptyString(selectedApps.PW_CONFIGURABLE);
              break;
            case 5: // Password Configuration
              if ((selectedApps.AUTHENTICATION_TYPE === 'Native/Direct' || 
                  selectedApps.AUTHENTICATION_TYPE === 'Both Native and SSO') && 
                  selectedApps.PW_CONFIGURABLE === 'Yes') {
                currentStepComplete = isValidInteger(passwordData.LENGTH) &&
                  isValidInteger(passwordData.AGE) &&
                  isValidInteger(passwordData.HISTORY) &&
                  isValidInteger(passwordData.LOCKOUT_ATTEMPT);
              } else {
                currentStepComplete = true;
              }
              break;
            default:
              currentStepComplete = false;
          }
          
          if (currentStepComplete) {
            // Handle skipped steps
            let newSkipped = new Set(skipped);
            if (skipped.has(activeStep)) {
              newSkipped.delete(activeStep);
            }
            
            setActiveStep(prev => prev + 1);
            setSkipped(newSkipped);
          }
        }
        
        // Previous step with left/up arrow keys
        else if ((e.key === 'ArrowLeft' || e.key === 'ArrowUp') && activeStep > 0) {
          setActiveStep(prev => prev - 1);
        }
      }
      
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [
      activeStep, 
      steps.length, 
      skipped,
      selectedApps.APP_TYPE,
      selectedApps.APP_DESCRIPTION,
      selectedApps.DEVTYPE,
      selectedApps.HOSTED,
      selectedApps.OS,
      selectedApps.OS_VERSION,
      selectedApps.DATABASE,
      selectedApps.DB_VERSION,
      selectedApps.NETWORK,
      selectedApps.NETWORK_VERSION,
      selectedApps.AUTHENTICATION_TYPE,
      selectedApps.PW_CONFIGURABLE,
      passwordData.LENGTH,
      passwordData.AGE,
      passwordData.HISTORY,
      passwordData.LOCKOUT_ATTEMPT,
      isNonEmptyString,
      isValidInteger
    ]);

    // Rendering question based on active step
    const renderQuestion = () => {
      switch(activeStep) {
        case 0:
          return (
            <>
              <QuestionTitle>
                <QuizIcon fontSize="medium" />
                What type of system are you auditing?
              </QuestionTitle>
              <QuestionDescription>
                Select the category that best describes your application or system.
              </QuestionDescription>
              <MinimalistSelect
                label="System Type"
                placeholderText="Select System Type*"
                selectOptions={typeOptions}
                selectedOptions={selectedType}
                handleChange={handleTypeChange}
                isMultiSelect={false}
              />
            </>
          );
          
        case 1:
          return (
            <>
              <QuestionTitle>
                <QuizIcon fontSize="medium" />
                Describe your application
              </QuestionTitle>
              <QuestionDescription>
                Provide a detailed description of what this application does, its purpose, and primary functions.
              </QuestionDescription>
              <MinimalistTextField
                label="Description"
                name="APP_DESCRIPTION"
                value={selectedApps.APP_DESCRIPTION}
                onChange={handleChange}
                isMultiLine={true}
                rows="4"
                onBlur={handleBlur}
                required
                error={!!validationErrors.APP_DESCRIPTION}
                helperText={validationErrors.APP_DESCRIPTION}
              />
            </>
          );
          
        case 2:
          return (
            <>
              <QuestionTitle>
                <QuizIcon fontSize="medium" />
                Development and Hosting Information
              </QuestionTitle>
              <QuestionDescription>
                Tell us about how this application was developed and where it's hosted.
              </QuestionDescription>
              <FieldGrid container spacing={3}>
                <mui.Grid item xs={12} sx={{ mb: 3 }}>
                  <MinimalistSelect
                    label="Development Type"
                    placeholderText="Select Development Type*"
                    selectOptions={developOptions}
                    selectedOptions={selectedDev}
                    handleChange={handleDevChange}
                    isMultiSelect={false}
                  />
                </mui.Grid>
                <mui.Grid item xs={12}>
                  <MinimalistSelect
                    label="Hosting Type"
                    placeholderText="Select Hosting Type*"
                    selectOptions={hostingOptions}
                    selectedOptions={selectedHosting}
                    handleChange={handleHostingChange}
                    isMultiSelect={false}
                  />
                </mui.Grid>
              </FieldGrid>
            </>
          );
          
        case 3:
          return (
            <>
              <QuestionTitle>
                <QuizIcon fontSize="medium" />
                Infrastructure Details
              </QuestionTitle>
              <QuestionDescription>
                Since your application is on-premise, we need information about the infrastructure it runs on.
              </QuestionDescription>
              <FieldGrid container spacing={3}>
                <mui.Grid item xs={12} md={6}>
                  <MinimalistSelect
                    label="Operating System"
                    placeholderText="Select Operating System*"
                    selectOptions={osList}
                    selectedOptions={selectedOS}
                    handleChange={handleOSChange}
                    isMultiSelect={false}
                  />
                </mui.Grid>
                <mui.Grid item xs={12} md={6}>
                  <MinimalistTextField
                    label="OS Version"
                    name="OS_VERSION"
                    value={selectedApps.OS_VERSION}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </mui.Grid>
                <mui.Grid item xs={12} md={6}>
                  <MinimalistSelect
                    label="Database"
                    placeholderText="Select Database*"
                    selectOptions={dbList}
                    selectedOptions={selectedDB}
                    handleChange={handleDBChange}
                    isMultiSelect={false}
                  />
                </mui.Grid>
                <mui.Grid item xs={12} md={6}>
                  <MinimalistTextField
                    label="Database Version"
                    name="DB_VERSION"
                    value={selectedApps.DB_VERSION}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </mui.Grid>
                <mui.Grid item xs={12} md={6}>
                  <MinimalistSelect
                    label="Network"
                    placeholderText="Select Network*"
                    selectOptions={networkList}
                    selectedOptions={selectedNetwork}
                    handleChange={handleNetworkChange}
                    isMultiSelect={false}
                  />
                </mui.Grid>
                <mui.Grid item xs={12} md={6}>
                  <MinimalistTextField
                    label="Network Version"
                    name="NETWORK_VERSION"
                    value={selectedApps?.NETWORK_VERSION}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </mui.Grid>
              </FieldGrid>
            </>
          );
          
        case 4:
          return (
            <>
              <QuestionTitle>
                <QuizIcon fontSize="medium" />
                Authentication Method
              </QuestionTitle>
              <QuestionDescription>
                How do users authenticate with your application?
              </QuestionDescription>
              <FieldGrid container spacing={3}>
                <mui.Grid item xs={12} sx={{ mb: 3 }}>
                  <MinimalistSelect
                    label="Authentication Method"
                    placeholderText="Select Login Method*"
                    selectOptions={loginOptions}
                    selectedOptions={selectedAuth}
                    handleChange={handleAuthChange}
                    isMultiSelect={false}
                  />
                </mui.Grid>
                <mui.Grid item xs={12}>
                  <MinimalistSelect
                    label="Password Settings Configurable"
                    placeholderText="Select Choice*"
                    selectOptions={configureOptions}
                    selectedOptions={selectedConfig}
                    handleChange={handleConfigChange}
                    isMultiSelect={false}
                  />
                </mui.Grid>
              </FieldGrid>
            </>
          );
          
        case 5:
          return (
            <>
              <QuestionTitle>
                <QuizIcon fontSize="medium" />
                Password Configuration
              </QuestionTitle>
              <QuestionDescription>
                What password policies are configured for your application?
              </QuestionDescription>
              <FieldGrid container spacing={3}>
                <mui.Grid item xs={12} md={6}>
                  <MinimalistTextField
                    label="Minimum Password Length"
                    name="LENGTH"
                    type="number"
                    value={passwordData?.LENGTH}
                    onChange={handleChangePWChange}
                    onBlur={handlePWBlur}
                    min={1}
                    error={!!validationErrors.LENGTH}
                    helperText={validationErrors.LENGTH}
                  />
                </mui.Grid>
                <mui.Grid item xs={12} md={6}>
                  <MinimalistTextField
                    label="Password Age (days)"
                    name="AGE"
                    type="number"
                    value={passwordData?.AGE}
                    onChange={handleChangePWChange}
                    onBlur={handlePWBlur}
                    min={1}
                    error={!!validationErrors.AGE}
                    helperText={validationErrors.AGE}
                  />
                </mui.Grid>
                <mui.Grid item xs={12} md={6}>
                  <MinimalistTextField
                    label="Password History"
                    name="HISTORY"
                    type="number"
                    value={passwordData?.HISTORY}
                    onChange={handleChangePWChange}
                    onBlur={handlePWBlur}
                    min={1}
                    error={!!validationErrors.HISTORY}
                    helperText={validationErrors.HISTORY}
                  />
                </mui.Grid>
                <mui.Grid item xs={12} md={6}>
                  <MinimalistTextField
                    label="Max Failed Login Attempts"
                    name="LOCKOUT_ATTEMPT"
                    type="number"
                    value={passwordData?.LOCKOUT_ATTEMPT}
                    onChange={handleChangePWChange}
                    onBlur={handlePWBlur}
                    min={1}
                    error={!!validationErrors.LOCKOUT_ATTEMPT}
                    helperText={validationErrors.LOCKOUT_ATTEMPT}
                  />
                </mui.Grid>
                <mui.Grid item xs={12} sx={{ mt: 2 }}>
                  <MinimalistSwitchesGroup 
                    title="Password Complexity Requirements" 
                    switches={switches} 
                    onChange={handleSwitchChange}
                  />
                </mui.Grid>
              </FieldGrid>
            </>
          );
          
        default:
          return (
            <mui.Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircleRoundedIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <mui.Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                Configuration Complete!
              </mui.Typography>
              <mui.Typography variant="body1" sx={{ mb: 4 }}>
                All required information has been collected. You can now proceed with your application audit.
              </mui.Typography>
              <mui.Button 
                variant="contained" 
                color="primary"
                onClick={handleReset}
              >
                Review Information
              </mui.Button>
            </mui.Box>
          );
      }
    };

    const Item = styled(Paper)(({ theme }) => ({
        backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
        ...theme.typography.body2,
        padding: theme.spacing(1),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    }));

    const handleBlur = (e) => {
        const { name, value } = e.target;

        // Validate the field if necessary
        if (name === 'APP_DESCRIPTION') {
            const validation = validateText(value, 'Description');
            if (!validation.valid) {
                return;
            }
        }

        const updatedData = {
            ...selectedApps,
            [name]: value
        };

        setSelectedApps(updatedData);
        
        // Save on blur to ensure data is saved even if
        // the user doesn't trigger the onChange debounce
        setIsSaving(true);
        setSaveComplete(false);
        saveChanges(updatedData);
    };

    // Validate functions
    const validateNumber = (value, fieldName, min = 1) => {
      // If no value is provided, it's valid (not required)
      if (value === undefined || value === null || value === '') {
        return { valid: true, message: '' };
      }
      
      const numValue = Number(value);
      
      if (isNaN(numValue)) {
        return { valid: false, message: `${fieldName} must be a number` };
      }
      
      if (numValue < min) {
        return { valid: false, message: `${fieldName} must be at least ${min}` };
      }
      
      return { valid: true, message: '' };
    };

    const validateText = (value, fieldName, minLength = 1) => {
      if (!value || value.trim().length < minLength) {
        return { valid: false, message: `${fieldName} is required` };
      }
      return { valid: true, message: '' };
    };

    // Enhanced handleChange with validation
    const handleChange = (e) => {
        const { name, value } = e.target;
        
        // Validate description field
        if (name === 'APP_DESCRIPTION') {
            const validation = validateText(value, 'Description');
            setValidationErrors(prev => ({
                ...prev,
                [name]: validation.valid ? null : validation.message
            }));
        }
        
        if (saveTimeout.current) {
            clearTimeout(saveTimeout.current);
        }

        const updatedData = {
            ...selectedApps,
            [name]: value
        };
              
        setSelectedApps(updatedData);
        setIsSaving(true);
        setSaveComplete(false);

        saveTimeout.current = setTimeout(() => {
            // Only save if there are no validation errors for this field
            if (!validationErrors[name]) {
                saveChanges(updatedData);
            }
        }, 1000);
    };

    const handleTypeChange = (selectedType) => {

        const apptype = selectedType.value
        setformattedType(selectedType)

        const updatedData = {
            ...selectedApps,
            APP_TYPE: apptype
        };
        saveChanges(updatedData);

    };

    const handleDevChange = (selectedType) => {

        const devtype = selectedType.value

        setSelectedDev(selectedType)

        const updatedData = {
            ...selectedApps,
            DEVTYPE: devtype
        };

        saveChanges(updatedData);
     
    };

    const handleHostingChange = (selectedType) => {

        const hosting = selectedType.value
        setSelectedHosting(selectedType)

        const visibility = hosting === 'On-premise' ? 'block' : 'none';
        setHostingTypeVisibility(visibility);


        const updatedData = {
            ...selectedApps,
            HOSTED: hosting
        };

        saveChanges(updatedData);

    };

    const handleOSChange = (selectedType) => {
        const OS = selectedType.value
        setSelectedOS(selectedType)

        const updatedData = {
            ...selectedApps,
            OS: OS
        };
        saveChanges(updatedData);
      
    };

    const handleDBChange = (selectedType) => {
        const DB = selectedType.value
        setSelectedDB(selectedType)

        const updatedData = {
            ...selectedApps,
            DATABASE: DB
        };
        saveChanges(updatedData);

    };

    const handleNetworkChange = (selectedType) => {
        const network = selectedType.value
        setSelectedNetwork(selectedType)

        const updatedData = {
            ...selectedApps,
            NETWORK: network
        };
        saveChanges(updatedData);
    };


    //AUTHENTICATION
    const handleAuthChange = (selectedType) => {
        const auth = selectedType.value
        setSelectedAuth(selectedType)

        const updatedData = {
            ...selectedApps,
            AUTHENTICATION_TYPE: auth
        };
        saveChanges(updatedData);
    };

    const handleConfigChange = (selectedType) => {
        const authconfigurable = selectedType.value
        setSelectedConfig(selectedType)

        const updatedData = {
            ...selectedApps,
            PW_CONFIGURABLE: authconfigurable
        };
        saveChanges(updatedData);
    };

    // TRIGGER THE CHECKMARK
    useEffect(() => {
        visibleCheckMark();
    },[id, selectedApps, passwordData]);


    //SAVE CHANGES TO THE APPLICATION RECORD with saving indicator and retry logic
    const saveChanges = async (updatedData, retryCount = 0) => {
        // Skip if another save is already in progress
        if (saveInProgress) {
            return;
        }
        
        setSaveInProgress(true);
        
        try {
            const response = await appService.updateApp(id, updatedData);
            if (response) {
                setSelectedApps(response);
                setSaveComplete(true);
                setTimeout(() => {
                    setIsSaving(false);
                    setTimeout(() => {
                        setSaveComplete(false);
                    }, 1000);
                }, 500);
                
                // Ensure the checkmark is updated based on the latest data
                visibleCheckMark();
            } else {
                throw new Error("Empty response received");
            }
        } catch (error) {
            console.error('Error saving changes:', error);
            
            // Retry logic - maximum 2 retries (3 attempts total)
            if (retryCount < 2) {
                console.log(`Retrying save... Attempt ${retryCount + 2}`);
                setTimeout(() => saveChanges(updatedData, retryCount + 1), 1000);
                return; // Return early to keep saveInProgress true
            } else {
                setIsSaving(false);
                setSaveComplete(false);
                // Show error message to user here if needed
            }
        } finally {
            if (retryCount === 2 || retryCount === 0) {
                // Only reset saveInProgress if this is the final attempt or successful first attempt
                setSaveInProgress(false);
            }
        }
    };

    //SAVE PASSWORD RECORD with saving indicator and retry logic
    const savePWChanges = async (updatedData, retryCount = 0) => {
        // Skip if another save is already in progress
        if (saveInProgress) {
            return;
        }
        
        setSaveInProgress(true);
        setIsSaving(true);
        setSaveComplete(false);
        
        try {
            let updatedRecord;
            
            // Ensure data has the right format
            const dataToSave = {
                ...updatedData,
                LENGTH: updatedData.LENGTH ? parseInt(updatedData.LENGTH, 10) : null,
                AGE: updatedData.AGE ? parseInt(updatedData.AGE, 10) : null,
                HISTORY: updatedData.HISTORY ? parseInt(updatedData.HISTORY, 10) : null,
                LOCKOUT_ATTEMPT: updatedData.LOCKOUT_ATTEMPT ? parseInt(updatedData.LOCKOUT_ATTEMPT, 10) : null,
                UPPER: !!updatedData.UPPER,  // Ensure boolean
                LOWER: !!updatedData.LOWER,  // Ensure boolean
                NUMBER: !!updatedData.NUMBER,  // Ensure boolean
                SPECIAL_CHAR: !!updatedData.SPECIAL_CHAR,  // Ensure boolean
                MFA_ENABLED: !!updatedData.MFA_ENABLED  // Ensure boolean
            };

            // Always add APP_NAME to the data for both update and create
            const newData = { ...dataToSave, APP_NAME: id };

            if (pwExist) {
                try {
                    // Try update first
                    updatedRecord = await appPasswordService.updateAppPassword(id, newData);
                    console.log("Updated Password");
                } catch (updateError) {
                    // If update fails, try to create instead
                    console.log("Update failed, trying to create:", updateError);
                    updatedRecord = await appPasswordService.createAppPassword(newData);
                    console.log("New Password Data Created after update failed");
                }
            } else {
                // Create new password data
                updatedRecord = await appPasswordService.createAppPassword(newData);
                console.log("New Password Data Created");
            }
            
            if (updatedRecord) {
                setPasswordData(updatedRecord);
                setPWExist(true);
                
                setSaveComplete(true);
                setTimeout(() => {
                    setIsSaving(false);
                    setTimeout(() => {
                        setSaveComplete(false);
                    }, 1000);
                }, 500);

                // Ensure the checkmark is updated
                visibleCheckMark();
            } else {
                throw new Error("No response received from password service");
            }
        } catch (error) {
            console.error('Error saving password data:', error);
            
            // Retry logic - maximum 2 retries (3 attempts total)
            if (retryCount < 2) {
                console.log(`Retrying password save... Attempt ${retryCount + 2}`);
                setTimeout(() => savePWChanges(updatedData, retryCount + 1), 1000);
                return; // Return early to keep saveInProgress true
            } else {
                setIsSaving(false);
                setSaveComplete(false);
                // Show error message to user here if needed
            }
        } finally {
            if (retryCount === 2 || retryCount === 0) {
                // Only reset saveInProgress if this is the final attempt or successful first attempt
                setSaveInProgress(false);
            }
        }
    };
    
    
    const typeOptions = [
        { value: 'Application', label: 'Application' },
        { value: 'Operating System', label: 'Operating System' },
        { value: 'Database', label: 'Database' },
        { value: 'Network', label: 'Network' },
        { value: 'Tool', label: 'Tool' }
    ];

    const hostingOptions = [
        { value: 'Vendor Hosted', label: 'Vendor Hosted' },
        { value: 'On-premise', label: 'On-premise' },
    ];

    const developOptions = [
        { value: 'Third-party Provided', label: 'Third-party Provided' },
        { value: 'Custom Made', label: 'Custom Made' },
    ];

    const loginOptions = [
        { value: 'Native/Direct', label: 'Native/Direct' },
        { value: 'Single-Sign-On (SSO)', label: 'Single-Sign-On (SSO)' },
        { value: 'Both Native and SSO', label: 'Both Native and SSO' },
    ];

    const configureOptions = [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
    ];

    useEffect(() => {
        const fetchData = async () => {
            const OS = 'Operating System';
            const DB = 'Database';
            const NETWORK = 'Network';
            const TOOL = 'Tool';
    
            // Helper function to fetch data with error handling
            const fetchWithErrorHandling = async (fetchFunction) => {
                try {
                    return await fetchFunction();
                } catch (error) {
                    console.error('Error fetching data:', error);
                    return null;
                }
            };
    
            // Fetch apps by ID
            const appsByIdResponse = await fetchWithErrorHandling(() => appService.fetchAppsById(id));
            
            // Fetch other categories concurrently
            const [OSResponse, DBResponse, NetworkResponse, ToolResponse] = await Promise.all([
                fetchWithErrorHandling(() => appService.fetchAppsByType(OS)),
                fetchWithErrorHandling(() => appService.fetchAppsByType(DB)),
                fetchWithErrorHandling(() => appService.fetchAppsByType(NETWORK)),
                fetchWithErrorHandling(() => appService.fetchAppsByType(TOOL)),
            ]);
    
            // Process appByIdResponse if available
            if (appsByIdResponse) {
                setSelectedApps(appsByIdResponse);
    
                const formatType = { value: appsByIdResponse.APP_TYPE, label: appsByIdResponse.APP_TYPE };
                setformattedType(formatType);
    
                const hostFormat = { value: appsByIdResponse.HOSTED, label: appsByIdResponse.HOSTED };
                setSelectedHosting(hostFormat);
    
                const devFormat = { value: appsByIdResponse.DEVTYPE, label: appsByIdResponse.DEVTYPE };
                setSelectedDev(devFormat);
    
                const authFormat = { value: appsByIdResponse.AUTHENTICATION_TYPE, label: appsByIdResponse.AUTHENTICATION_TYPE };
                setSelectedAuth(authFormat);
    
                const configFormat = { value: appsByIdResponse.PW_CONFIGURABLE, label: appsByIdResponse.PW_CONFIGURABLE };
                setSelectedConfig(configFormat);
    
                const hosting = appsByIdResponse.HOSTED;
                const visibility = hosting === 'On-premise' ? 'block' : 'none';
                setHostingTypeVisibility(visibility);
            }
    
            // Process appPWResponse if available
           
    
            // Process OSResponse if available
            if (OSResponse) {
                const formatOSList = OSResponse.map(item => ({
                    value: item.id,
                    label: item.APP_NAME
                }));
                setOSList(formatOSList);
    
                const selectedOS = await fetchWithErrorHandling(() => appService.fetchAppsById(appsByIdResponse?.OS));
                if (selectedOS) {
                    const formatOS = { value: selectedOS.id, label: selectedOS.APP_NAME };
                    setSelectedOS(formatOS);
                }
            } else {
                setOSList([]);
                setSelectedOS(null);
            }
    
            // Process DBResponse if available
            if (DBResponse) {
                const formatDBList = DBResponse.map(item => ({
                    value: item.id,
                    label: item.APP_NAME
                }));
                setDBList(formatDBList);
    
                const selectedDB = await fetchWithErrorHandling(() => appService.fetchAppsById(appsByIdResponse?.DATABASE));
                if (selectedDB) {
                    const formatDB = { value: selectedDB.id, label: selectedDB.APP_NAME };
                    setSelectedDB(formatDB);
                }
            } else {
                setDBList([]);
                setSelectedDB(null);
            }
    
            // Process NetworkResponse if available
            if (NetworkResponse) {
                const formatNetworkList = NetworkResponse.map(item => ({
                    value: item.id,
                    label: item.APP_NAME
                }));
                setNetworkList(formatNetworkList);
    
                const selectedNetwork = await fetchWithErrorHandling(() => appService.fetchAppsById(appsByIdResponse?.NETWORK));
                if (selectedNetwork) {
                    const formatNetwork = { value: selectedNetwork.id, label: selectedNetwork.APP_NAME };
                    setSelectedNetwork(formatNetwork);
                }
            } else {
                setNetworkList([]);
                setSelectedNetwork(null);
            }
    
            // Process ToolResponse (you can add further handling if needed)
            if (ToolResponse) {
               
                // Additional tool-related logic can go here if needed
            } else {
                setToolList([]);
            }
        };

        const fetchAppPassword = async () => {
            try {
                const appPWResponse = await appPasswordService.fetchAppPasswordbyID(id);
                console.log('This is the password data', appPWResponse);

                // Check if the response contains actual data and not an error
                if (appPWResponse && !appPWResponse.detail) {
                    setPasswordData(appPWResponse);
                    setPWExist(true);
                } else {
                    // Initialize with empty values when no record exists
                    setPasswordData({});
                    setPWExist(false);
                }
            } catch (error) {
                console.error(`Error fetching password data: ${error.message}`);
                // Initialize with empty values when error occurs
                setPasswordData({});
                setPWExist(false);
            }
        };

        fetchData();
        fetchAppPassword();
   
    }, [id]);
    

    const handleHostingTypeVisibility = () => {
        return hostingTypeVisibility;
    };

    const handleAuthenticationVisibility = () => {
        return selectedApps.AUTHENTICATION_TYPE === 'Native/Direct' ? 'block' : 'none';
    };

    const switches = [
        { name: 'UPPER', label: 'Upper Case', checked: passwordData.UPPER },
        { name: 'LOWER', label: 'Lower Case', checked: passwordData.LOWER },
        { name: 'NUMBER', label: 'Numeric', checked: passwordData.NUMBER },
        { name: 'SPECIAL_CHAR', label: 'Special Character', checked: passwordData.SPECIAL_CHAR },
        { name: 'MFA_ENABLED', label: 'MFA Enabled', checked: passwordData.MFA_ENABLED },
    ];

    const handleSwitchChange = (name, checked) => {
        const updatedData = {
            ...passwordData,
            [name]: checked
        };
        setPasswordData(updatedData)
        savePWChanges(updatedData);
    };

    // Enhanced handleChangePWChange with validation
    const handleChangePWChange = (e) => {
        const { name, value } = e.target;
        
        // Validate numeric fields
        if (['LENGTH', 'AGE', 'HISTORY', 'LOCKOUT_ATTEMPT'].includes(name)) {
            const validation = validateNumber(value, name);
            setValidationErrors(prev => ({
                ...prev,
                [name]: validation.valid ? null : validation.message
            }));
            
            // If invalid, don't save
            if (!validation.valid) {
                const updatedData = {
                    ...passwordData,
                    [name]: value
                };
                setPasswordData(updatedData);
                return;
            }
        }

        if (saveTimeout.current) {
            clearTimeout(saveTimeout.current);
        }

        const updatedData = {
            ...passwordData,
            [name]: value
        };

        setPasswordData(updatedData);
        setIsSaving(true);
        setSaveComplete(false);

        // Save changes with the updated state
        saveTimeout.current = setTimeout(() => {
            savePWChanges(updatedData);
        }, 1000); 
    };

    // Enhanced handlePWBlur with proper saving
    const handlePWBlur = (e) => {
        const { name, value } = e.target;

        // Validate if it's a numeric field
        if (['LENGTH', 'AGE', 'HISTORY', 'LOCKOUT_ATTEMPT'].includes(name)) {
            const validation = validateNumber(value, name);
            setValidationErrors(prev => ({
                ...prev,
                [name]: validation.valid ? null : validation.message
            }));
            
            if (!validation.valid) {
                return;
            }
        }

        const updatedData = {
            ...passwordData,
            [name]: value
        };
        
        setPasswordData(updatedData);
        setIsSaving(true);
        setSaveComplete(false);
        savePWChanges(updatedData);
    };

    const visibleCheckMark = async () => {

        let generalInfoValid = false;
        let infrastructureValid = false;
        let authenticationValid = false;
       
        //GENERAL INFO
        if (
            isNonEmptyString(selectedApps.APP_NAME) &&
            isNonEmptyString(selectedApps.APP_TYPE) &&
            isNonEmptyString(selectedApps.APP_DESCRIPTION)
        )
            {
            setOverview(true);
            generalInfoValid = true;
        } else {
            setOverview(false);
            generalInfoValid = false;
        }

        //INFRASTRUCTURE
        if (
            isNonEmptyString(selectedApps.DEVTYPE) &&
            selectedApps.HOSTED === 'On-premise'
        ) {
            if (
                isNonEmptyString(selectedApps.OS) &&
                isNonEmptyString(selectedApps.OS_VERSION) &&
                isNonEmptyString(selectedApps.DATABASE) &&
                isNonEmptyString(selectedApps.DB_VERSION) && 
                isNonEmptyString(selectedApps.NETWORK) && 
                isNonEmptyString(selectedApps.NETWORK_VERSION) 
            ) {
                setInfrastructure(true);
                infrastructureValid = true;
              
            } else {
                setInfrastructure(false);
                infrastructureValid = false;
                
            }
        } else if (
            isNonEmptyString(selectedApps.DEVTYPE) &&
            selectedApps.HOSTED === 'Vendor Hosted'
        ) {
            setInfrastructure(true);
            infrastructureValid = true;
            
        } else {
            setInfrastructure(false);
            infrastructureValid = false;
           
        }
        
        //PASSWORD
        if (
            selectedApps.AUTHENTICATION_TYPE === 'Single-Sign-On (SSO)' && 
            selectedApps.PW_CONFIGURABLE === 'No'
        ) {
            setAuthentication(true)
            authenticationValid = true;
            
        } else if (
            selectedApps.PW_CONFIGURABLE === 'Yes' &&
            isValidInteger(passwordData.LENGTH) &&
            isValidInteger(passwordData.AGE) &&
            isValidInteger(passwordData.HISTORY) &&
            isValidInteger(passwordData.LOCKOUT_ATTEMPT) 
        ) {
            setAuthentication(true)
            authenticationValid = true;
        }
        else {
            setAuthentication(false)
            authenticationValid = false;
           
        }

        if (generalInfoValid && infrastructureValid &&authenticationValid ) {
            console.log("General information is ready for audit")

            const updatedData = {
                ...selectedApps,
                SETUP_GENINFO: true
            };
            
            const gen_info_ready = await appService.updateApp(id, updatedData )

        } else {
            console.log('Need to be updated')

            const updatedData = {
                ...selectedApps,
                SETUP_GENINFO: false
            };

            const gen_info_ready = await appService.updateApp(id, updatedData )
        }

    };

    // Helper function to determine active step
    const getActiveStep = () => {
        let step = 0;
        if (overview) step = 1;
        if (infrastructure) step = 2;
        if (authentication) step = 3;
        return step-1 >= 0 ? step-1 : 0;
    };

    // Calculate progress percentage
    const getProgressPercentage = () => {
        let completed = 0;
        if (overview) completed++;
        if (infrastructure) completed++;
        if (authentication) completed++;
        return Math.round((completed / 3) * 100);
    };

    const customMainContent = (
        <div>
            <ResponsiveContainer>
                <mui.Box sx={{ padding: '24px' }}>
                    <mui.Breadcrumbs aria-label="breadcrumb">
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            <i className="material-icons">home</i>
                        </mui.Link>
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            My Dashboard
                        </mui.Link>
                        <mui.Link underline="hover" color="inherit" href="/applications">
                            My Applications
                        </mui.Link>
                        <mui.Typography color="text.primary">{selectedApps.APP_NAME}</mui.Typography>
                    </mui.Breadcrumbs>

                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 4, mt: 3 }}>
                        <GridViewIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                        <mui.Box>
                            <mui.Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                                {selectedApps.APP_NAME}
                            </mui.Typography>
                            <mui.Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                Please answer the following questions about your application
                            </mui.Typography>
                        </mui.Box>
                    </mui.Box>

                    <mui.Box sx={{ 
                        backgroundColor: '#f8fafc', 
                        borderRadius: 3,
                        minHeight: '80vh',
                        boxShadow: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)',
                        p: 3
                    }}>
                        {/* Progress Stepper */}
                        <mui.Box sx={{ width: '100%', mb: 5 }}>
                            {/* Progress indicator */}
                            <mui.Box sx={{ mb: 3 }}>
                                <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <mui.Typography variant="body2" color="text.secondary">
                                        Progress
                                    </mui.Typography>
                                    <mui.Typography variant="body2" fontWeight={500} color="primary">
                                        {Math.round((steps.filter((_, index) => isStepComplete(index)).length / steps.length) * 100)}%
                                    </mui.Typography>
                                </mui.Box>
                                <mui.LinearProgress 
                                    variant="determinate" 
                                    value={(steps.filter((_, index) => isStepComplete(index)).length / steps.length) * 100}
                                    sx={{ 
                                        height: 8, 
                                        borderRadius: 4,
                                        backgroundColor: alpha('#000', 0.05),
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 4,
                                        }
                                    }}
                                />
                            </mui.Box>
                            
                            <mui.Stepper 
                                activeStep={activeStep} 
                                alternativeLabel
                                connector={
                                    <mui.StepConnector 
                                        sx={{
                                            '& .MuiStepConnector-line': {
                                                height: 3,
                                                borderTopWidth: 3,
                                                borderRadius: 1,
                                                borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                                            },
                                            '&.Mui-active .MuiStepConnector-line': {
                                                borderColor: (theme) => theme.palette.primary.main,
                                            },
                                            '&.Mui-completed .MuiStepConnector-line': {
                                                borderColor: (theme) => theme.palette.success.main,
                                            },
                                        }}
                                    />
                                }
                            >
                                {steps.map((step, index) => {
                                    const stepProps = {};
                                    const labelProps = {};
                                    
                                    if (isStepOptional(index)) {
                                        labelProps.optional = (
                                            <mui.Typography variant="caption">Optional</mui.Typography>
                                        );
                                    }
                                    
                                    if (isStepSkipped(index)) {
                                        stepProps.completed = false;
                                    }
                                    
                                    return (
                                        <mui.Step key={step.label} {...stepProps}>
                                            <mui.Tooltip 
                                                title={
                                                    isStepSkipped(index) ? "This step is skipped" :
                                                    isStepComplete(index) ? "This step is completed. Click to edit." :
                                                    index > activeStep ? "Complete previous steps first" :
                                                    index === activeStep ? "Current step" :
                                                    "Click to go back to this step"
                                                }
                                                arrow
                                                placement="top"
                                            >
                                                <span> {/* Wrapper required for Tooltip on disabled elements */}
                                                    <CustomStepButton
                                                        onClick={() => {
                                                            // Don't allow navigating to steps that would be skipped
                                                            if (isStepSkipped(index)) return;
                                                            
                                                            // Don't allow navigating to future steps that require previous steps
                                                            if (index > activeStep) {
                                                                for (let i = 0; i < index; i++) {
                                                                    if (!isStepComplete(i) && !isStepSkipped(i)) {
                                                                        return;
                                                                    }
                                                                }
                                                            }
                                                            
                                                            setActiveStep(index);
                                                        }}
                                                        completed={isStepComplete(index)}
                                                        active={activeStep === index}
                                                        optional={isStepOptional(index) ? 
                                                            <mui.Typography variant="caption">Optional</mui.Typography> : null
                                                        }
                                                    >
                                                        {step.label}
                                                    </CustomStepButton>
                                                </span>
                                            </mui.Tooltip>
                                        </mui.Step>
                                    );
                                })}
                            </mui.Stepper>
                        </mui.Box>
                        
                        {/* Question Card */}
                        <mui.Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <QuestionnaireCard
                                elevation={0}
                                sx={{ 
                                    width: '100%', 
                                    maxWidth: '800px', 
                                    minHeight: '400px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    p: { xs: 3, sm: 4 }
                                }}
                            >
                                {/* Quick Jump Menu */}
                                <mui.Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                                    <mui.Button
                                        variant="outlined"
                                        size="small"
                                        endIcon={<ArrowDropDownIcon />}
                                        onClick={handleMenuOpen}
                                        sx={{ 
                                            borderRadius: '18px', 
                                            fontSize: '0.75rem',
                                            color: 'text.secondary',
                                            borderColor: alpha('#000', 0.12),
                                            '&:hover': {
                                                backgroundColor: alpha('#000', 0.03),
                                                borderColor: alpha('#000', 0.2),
                                            }
                                        }}
                                    >
                                        Quick Jump
                                    </mui.Button>
                                    <mui.Menu
                                        id="quick-jump-menu"
                                        anchorEl={menuAnchorEl}
                                        open={isMenuOpen}
                                        onClose={handleMenuClose}
                                    >
                                        {steps.map((step, index) => (
                                            <mui.MenuItem
                                                key={step.label}
                                                disabled={
                                                    isStepSkipped(index) || 
                                                    (index > activeStep && !steps.slice(0, index).every((_, i) => isStepComplete(i) || isStepSkipped(i)))
                                                }
                                                onClick={() => {
                                                    if (!isStepSkipped(index) && 
                                                        !(index > activeStep && !steps.slice(0, index).every((_, i) => isStepComplete(i) || isStepSkipped(i)))) {
                                                        setActiveStep(index);
                                                        handleMenuClose();
                                                    }
                                                }}
                                                sx={{ 
                                                    minWidth: '180px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1.5,
                                                    color: activeStep === index ? 'primary.main' : 'text.primary',
                                                    fontWeight: activeStep === index ? 500 : 400,
                                                }}
                                            >
                                                {isStepComplete(index) && (
                                                    <CheckCircleRoundedIcon 
                                                        fontSize="small" 
                                                        color="success" 
                                                        sx={{ fontSize: '1rem' }} 
                                                    />
                                                )}
                                                {!isStepComplete(index) && (
                                                    <mui.Box 
                                                        sx={{ 
                                                            width: '1rem', 
                                                            height: '1rem', 
                                                            borderRadius: '50%',
                                                            bgcolor: activeStep === index ? 'primary.main' : alpha('#000', 0.1),
                                                            fontSize: '0.75rem',
                                                            color: 'white',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        {index + 1}
                                                    </mui.Box>
                                                )}
                                                <span>{step.label}</span>
                                            </mui.MenuItem>
                                        ))}
                                    </mui.Menu>
                                </mui.Box>
                                
                                {/* Card Content */}
                                <mui.Box sx={{ flexGrow: 1 }}>
                                    {renderQuestion()}
                                </mui.Box>
                                
                                {/* Navigation Buttons */}
                                {activeStep < steps.length && (
                                    <NavigationButtons>
                                        <mui.Button
                                            color="inherit"
                                            disabled={activeStep === 0}
                                            onClick={handleBack}
                                            variant="outlined"
                                            startIcon={<NavigateBeforeIcon />}
                                            sx={{ borderRadius: '28px', px: 3 }}
                                        >
                                            Previous
                                        </mui.Button>
                                        
                                        <mui.Box>
                                            {isStepOptional(activeStep) && (
                                                <mui.Button 
                                                    color="inherit" 
                                                    onClick={handleSkip}
                                                    sx={{ mr: 1 }}
                                                    variant="text"
                                                >
                                                    Skip
                                                </mui.Button>
                                            )}
                                            
                                            <mui.Button
                                                variant="contained"
                                                onClick={handleNext}
                                                endIcon={<NavigateNextIcon />}
                                                disabled={!isStepComplete(activeStep)}
                                                sx={{ borderRadius: '28px', px: 3 }}
                                            >
                                                {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
                                            </mui.Button>
                                        </mui.Box>
                                    </NavigationButtons>
                                )}
                                
                                {/* Keyboard navigation hint */}
                                <mui.Box sx={{ textAlign: 'center', mt: 2 }}>
                                    <mui.Typography variant="caption" color="text.secondary">
                                        Pro tip: Use ← → arrow keys to navigate between steps
                                    </mui.Typography>
                                </mui.Box>
                            </QuestionnaireCard>
                        </mui.Box>
                    </mui.Box>
                </mui.Box>
                
                {/* Saving indicator */}
                <mui.Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    {/* Add "Go Back" button */}
                    <mui.Button 
                        variant="outlined"
                        color="primary"
                        onClick={() => navigate('/applications')}
                        startIcon={<NavigateBeforeIcon />}
                        sx={{ 
                            fontWeight: 500,
                            borderRadius: 2,
                            textTransform: 'none'
                        }}
                    >
                        Back to Applications
                    </mui.Button>
                </mui.Box>
                <SavingIndicator
                    in={isSaving || saveComplete}
                    timeout={{ enter: 500, exit: 500 }}
                    unmountOnExit
                >
                    <mui.Paper 
                        elevation={4} 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            padding: '10px 20px', 
                            backgroundColor: saveComplete ? '#f8fffa' : '#f5f9ff',
                            border: `1px solid ${saveComplete ? '#d7f0e0' : '#dae7fb'}`,
                            color: saveComplete ? '#43a047' : '#1976d2',
                            borderRadius: '10px',
                            minWidth: '180px',
                            maxWidth: '280px',
                            boxShadow: saveComplete 
                                ? '0 6px 20px rgba(67, 160, 71, 0.1)' 
                                : '0 6px 20px rgba(25, 118, 210, 0.1)',
                            transition: 'all 0.3s ease',
                        }}
                    >
                        {saveComplete ? (
                            <>
                                <CheckCircleRoundedIcon color="success" sx={{ mr: 1.5 }} fontSize="small" />
                                <mui.Typography 
                                    variant="body2" 
                                    fontWeight={500}
                                    sx={{ 
                                        fontSize: '0.875rem', 
                                        color: saveComplete ? 'success.main' : 'primary.main',
                                    }}
                                >
                                    Changes saved
                                </mui.Typography>
                            </>
                        ) : (
                            <>
                                <mui.CircularProgress
                                    size={16} 
                                    thickness={6}
                                    sx={{ 
                                        mr: 1.5, 
                                        color: 'primary.main',
                                    }} 
                                />
                                <mui.Typography 
                                    variant="body2" 
                                    fontWeight={500}
                                    sx={{ 
                                        fontSize: '0.875rem', 
                                        color: 'primary.main'
                                    }}
                                >
                                    Saving changes...
                                </mui.Typography>
                            </>
                        )}
                    </mui.Paper>
                </SavingIndicator>
            </ResponsiveContainer>
        </div>
    );

    return (
        <div>
            <SysOwnerSideBar mainContent={customMainContent} />
        </div>
    );
};

export default MyApplicationGeneralInfo;
